import type { Todo } from "@listto/powersync";
import type { VirtualRow } from "../types";

// ── Constants ─────────────────────────────────────────────────────────────────

export const TODO_PAGE_SIZE = 100;
// Uniform vertical gap between every row — single source of truth for the
// spacing between todos. Added once per row in `rowPositions`, never baked
// into a card's own margin, so the separation is identical for all rows.
export const ROW_GAP_PX = 6;
// Estimated *content* heights (without the gap). Used only until a row is
// measured, to avoid a visible jump while fast-scrolling. Real heights come
// from a ResizeObserver, so dynamic content (wrapped badges, long names) never
// overlaps the next row nor leaves an oversized gap.
export const EST_ROW_TODO = 56; // matches GlassSurface min-h-[56px]
export const EST_ROW_TODO_WITH_BADGES = 74; // card + single badge row
export const EST_ROW_HEADER = 44; // matches completed-header button min-h-[44px]
// Rows rendered beyond the viewport on each side. Lower = fewer expensive
// GlassSurface (backdrop-filter) layers composited at once while scrolling.
export const OVERSCAN_PX = 600;
export const INITIAL_CONTAINER_HEIGHT = 800;
export const MIN_STICKY_THRESHOLD_PX = 40;
export const STICKY_HEADER_OFFSET_PX = 10;
export const INNER_DIV_PT_PX = 16; // pt-4 = 1rem = 16px

export const MUTATION_OPTIONS_INDEX = 3;

const HALF = 2;

// ── Private helpers ───────────────────────────────────────────────────────────

/** Stable measurement key per row: todo id, or a constant for the lone header. */
export const rowKey = (row: VirtualRow) =>
  row.kind === "completed-header" ? "completed-header" : row.todo.id;

/**
 * Cheap render-equivalence fingerprint for a todo list. A single mutation makes
 * PowerSync invalidate the query several times in a row; each refetch returns a
 * brand-new array with identical contents. Comparing this signature lets us
 * skip the redundant updates so the list isn't recomputed/reconciled (and the
 * entrance animation isn't disturbed) for data that hasn't actually changed.
 * `updated_at` bumps on every write, so it captures any real change.
 */
export const todosSignature = (items: readonly Todo[]) =>
  items
    .map((t) => `${t.id}:${t.updated_at ?? ""}:${t.completed ?? 0}:${t.important ?? 0}`)
    .join("|");

/**
 * Binary search over row positions (sorted by `start`, monotonically
 * increasing) for the first row whose bottom edge is below `windowTop`.
 * Lets the virtualizer find the visible window in O(log n) instead of
 * scanning every loaded row on each scroll frame.
 */
export const findFirstVisibleRow = (
  positions: readonly { start: number; height: number }[],
  windowTop: number,
) => {
  let lo = 0;
  let hi = positions.length - 1;
  let result = positions.length;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / HALF);
    const pos = positions[mid];
    if (pos && pos.start + pos.height > windowTop) {
      result = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return result;
};

export const getTodoNavigationTarget = (listId: string, todoId: string) => {
  switch (listId) {
    case "important": {
      return { to: "/important/todo/$todoId", params: { todoId } };
    }
    case "my-day": {
      return { to: "/my-day/todo/$todoId", params: { todoId } };
    }
    case "inbox": {
      return { to: "/inbox/todo/$todoId", params: { todoId } };
    }
    case "all": {
      return { to: "/all/todo/$todoId", params: { todoId } };
    }
    case "planned": {
      return { to: "/planned/todo/$todoId", params: { todoId } };
    }
    default: {
      return { to: "/lists/$listId/todo/$todoId", params: { listId, todoId } };
    }
  }
};
